import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Play, Pause, Volume2, Settings as SettingsIcon, QrCode } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

export default function BingoGame() {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentNumber, setCurrentNumber] = useState<number | null>(null);
  const [calledNumbers, setCalledNumbers] = useState<number[]>([]);
  const [gameMode, setGameMode] = useState<"fullhouse" | "line">("fullhouse");

  const bingoCallPhrases: { [key: number]: string } = {
    11: "Legs Eleven",
    22: "Two Little Ducks",
    88: "Two Fat Ladies",
    13: "Unlucky for Some",
    77: "Sunset Strip",
  };

  const handleCallNumber = () => {
    // Simulate calling a random number
    const availableNumbers = Array.from({ length: 90 }, (_, i) => i + 1).filter(
      n => !calledNumbers.includes(n)
    );
    
    if (availableNumbers.length > 0) {
      const randomNumber = availableNumbers[Math.floor(Math.random() * availableNumbers.length)];
      setCurrentNumber(randomNumber);
      setCalledNumbers([...calledNumbers, randomNumber]);
    }
  };

  const togglePlay = () => {
    setIsPlaying(!isPlaying);
    if (!isPlaying) {
      handleCallNumber();
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Digital Bingo Caller</h1>
        <p className="text-muted-foreground mt-2">
          Interactive bingo caller with animations and audio
        </p>
        <Badge className="mt-2 bg-primary">Standard Plan Feature</Badge>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Caller */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="shadow-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Bingo Caller</CardTitle>
                  <CardDescription>Call numbers with animations and audio</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={gameMode === "fullhouse" ? "default" : "outline"}>
                    {gameMode === "fullhouse" ? "Full House" : "Line"}
                  </Badge>
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="icon">
                        <SettingsIcon className="h-4 w-4" />
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Bingo Settings</DialogTitle>
                        <DialogDescription>Configure your bingo game</DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label>Game Mode</Label>
                          <Select value={gameMode} onValueChange={(v: "fullhouse" | "line") => setGameMode(v)}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="fullhouse">Full House</SelectItem>
                              <SelectItem value="line">Single Line</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Call Speed</Label>
                          <Select defaultValue="normal">
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="slow">Slow (10s)</SelectItem>
                              <SelectItem value="normal">Normal (5s)</SelectItem>
                              <SelectItem value="fast">Fast (3s)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex items-center justify-between">
                          <Label>Audio Calls</Label>
                          <Switch defaultChecked />
                        </div>
                        <div className="flex items-center justify-between">
                          <Label>Auto Play</Label>
                          <Switch />
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Current Number Display */}
                <div className="bg-gradient-primary rounded-lg p-12 text-center">
                  {currentNumber ? (
                    <div className="animate-fade-in">
                      <div className="text-8xl font-bold text-primary-foreground mb-4">
                        {currentNumber}
                      </div>
                      {bingoCallPhrases[currentNumber] && (
                        <p className="text-2xl text-primary-foreground/90">
                          {bingoCallPhrases[currentNumber]}
                        </p>
                      )}
                    </div>
                  ) : (
                    <div>
                      <p className="text-3xl text-primary-foreground/70">
                        Ready to start?
                      </p>
                      <p className="text-primary-foreground/60 mt-2">
                        Press play to begin calling
                      </p>
                    </div>
                  )}
                </div>

                {/* Controls */}
                <div className="flex gap-3">
                  <Button
                    onClick={togglePlay}
                    size="lg"
                    className="flex-1"
                    disabled={calledNumbers.length >= 90}
                  >
                    {isPlaying ? (
                      <>
                        <Pause className="h-5 w-5 mr-2" />
                        Pause
                      </>
                    ) : (
                      <>
                        <Play className="h-5 w-5 mr-2" />
                        {calledNumbers.length > 0 ? "Resume" : "Start Game"}
                      </>
                    )}
                  </Button>
                  
                  {!isPlaying && calledNumbers.length > 0 && (
                    <Button onClick={handleCallNumber} size="lg" variant="outline">
                      Next Number
                    </Button>
                  )}

                  <Button variant="outline" size="lg">
                    <Volume2 className="h-5 w-5" />
                  </Button>
                </div>

                {/* Progress */}
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-muted-foreground">Numbers Called</span>
                    <span className="font-medium">{calledNumbers.length} / 90</span>
                  </div>
                  <div className="h-2 bg-secondary rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-primary transition-all" 
                      style={{ width: `${(calledNumbers.length / 90) * 100}%` }}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Number Board */}
          <Card className="shadow-card">
            <CardHeader>
              <CardTitle>Called Numbers</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-10 gap-2">
                {Array.from({ length: 90 }, (_, i) => i + 1).map((num) => {
                  const isCalled = calledNumbers.includes(num);
                  const isCurrently = currentNumber === num;
                  return (
                    <div
                      key={num}
                      className={`aspect-square flex items-center justify-center rounded text-sm font-semibold transition-all ${
                        isCurrently
                          ? "bg-primary text-primary-foreground scale-110 shadow-lg"
                          : isCalled
                          ? "bg-primary/20 text-primary"
                          : "bg-muted text-muted-foreground"
                      }`}
                    >
                      {num}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Side Panel */}
        <div className="space-y-6">
          {/* QR Code for Players */}
          <Card className="shadow-card">
            <CardHeader>
              <CardTitle>Player Access</CardTitle>
              <CardDescription>Let parents join via phone</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="aspect-square bg-muted rounded-lg flex items-center justify-center mb-4">
                <QrCode className="h-24 w-24 text-muted-foreground" />
              </div>
              <p className="text-sm text-center text-muted-foreground mb-4">
                Scan to play bingo on your phone
              </p>
              <Button variant="outline" className="w-full">Generate QR Code</Button>
              
              <div className="mt-4 pt-4 border-t border-border">
                <p className="text-xs text-muted-foreground mb-2">Or share link:</p>
                <code className="text-xs bg-muted p-2 rounded block">
                  ptahub.app/bingo/play/abc123
                </code>
              </div>
            </CardContent>
          </Card>

          {/* Game Stats */}
          <Card className="shadow-card">
            <CardHeader>
              <CardTitle>Game Statistics</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Players Active</span>
                <span className="font-semibold">24</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Paper Tickets</span>
                <span className="font-semibold">15</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Digital Tickets</span>
                <span className="font-semibold">9</span>
              </div>
              <div className="pt-3 border-t border-border">
                <Button variant="outline" className="w-full">View Players</Button>
              </div>
            </CardContent>
          </Card>

          {/* Recent Winners */}
          <Card className="shadow-card">
            <CardHeader>
              <CardTitle>Recent Claims</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="p-3 bg-muted/30 rounded">
                  <p className="font-medium text-sm">Line - Sarah Smith</p>
                  <p className="text-xs text-muted-foreground">2 minutes ago</p>
                </div>
                <p className="text-sm text-center text-muted-foreground py-2">
                  No full house winner yet
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
